/**
 * <back-in-stock-form>
 *
 * Handles submission of the "Notify me when it's back in stock" form.
 * Submits phone + requested size (plus product context) to a Google Apps
 * Script Web App endpoint, which logs the request to a Google Sheet and
 * emails the store.
 *
 * The endpoint URL is read from the `data-endpoint` attribute, rendered from
 * the `back_in_stock_webhook_url` theme setting.
 *
 * Requests are sent with mode "no-cors" because Apps Script Web Apps don't
 * return CORS headers, so the response can't be read. Network failures are
 * still detected; otherwise a success message is shown once the request is sent.
 */
/**
 * Shared family loader (also defined in rk-style-picker.js, whichever loads first wins).
 * Fetches every product of a family once per page, e.g. all Sambas, with their
 * in-stock sizes, from /collections/all/<family-tag>?section_id=rk-size-alternatives.
 */
window.rkLoadFamily = window.rkLoadFamily || (function () {
  const cache = {};

  return function (family) {
    if (!family) return Promise.resolve([]);
    if (cache[family]) return cache[family];

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = root + 'collections/all/' + encodeURIComponent(family) + '?section_id=rk-size-alternatives';

    cache[family] = fetch(url, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const products = [];

        doc.querySelectorAll('script[data-rk-alt]').forEach((script) => {
          try {
            products.push(JSON.parse(script.textContent));
          } catch (error) {
            // Skip a product with malformed data rather than breaking the list
          }
        });

        return products;
      })
      .catch(() => []);

    return cache[family];
  };
})();

class BackInStockForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');

    if (!this.form) return;

    this.submitButton = this.form.querySelector('[type="submit"]');
    this.bannerRegion = this.querySelector('[data-back-in-stock-banner]');

    this.countrySelect = this.querySelector('[data-country-select]');
    this.countryFlag = this.querySelector('[data-country-flag]');
    this.countryDial = this.querySelector('[data-country-dial]');

    if (this.countrySelect) {
      this.countrySelect.addEventListener('change', this.updateCountryDisplay.bind(this));
      this.updateCountryDisplay();
    }

    this.form.addEventListener('submit', this.onSubmit.bind(this));

    // Show/hide when the customer picks another size
    this.onVariantChange = this.onVariantChange.bind(this);
    document.addEventListener('variant:change', this.onVariantChange);

    // Similar styles in the selected size
    this.altsBox = this.querySelector('[data-bis-alts]');
    this.altsList = this.querySelector('[data-bis-alts-list]');
    this.altsTitle = this.querySelector('[data-bis-alts-title]');
    this.altsMode = this.dataset.altsMode === 'product' ? 'product' : 'size';
    this.sizeSelect = this.querySelector('[data-size-select]');

    if (this.altsMode === 'product') {
      // Whole product sold out: show the family's in-stock styles, any size
      this.renderAlternatives(null);
    } else {
      if (this.sizeSelect) {
        this.sizeSelect.addEventListener('change', () => this.renderAlternatives(this.sizeSelect.value));
      }

      // A sold-out size already selected on load (e.g. from the URL)
      if (!this.hidden && this.sizeSelect) {
        this.renderAlternatives(this.sizeSelect.value);
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariantChange);
  }

  /**
   * The theme fires "variant:change" on the product form whenever a size is
   * picked. Show this form only for a sold-out size, and preselect that size.
   */
  onVariantChange(event) {
    // Note: use getAttribute('id'), not .id. The product form contains an
    // input named "id", so form.id returns that input instead of the form ID.
    const productForm = this.dataset.productForm;
    const target = event.target;
    if (!productForm || !target || !target.getAttribute || target.getAttribute('id') !== productForm) return;

    const variant = event.detail && event.detail.variant;
    const isSoldOut = Boolean(variant && variant.id && variant.available === false);

    this.hidden = !isSoldOut;
    if (!isSoldOut) return;

    // Reset after a previous submission so they can sign up for this size too
    this.form.hidden = false;
    this.clearBanner();
    this.setLoading(false);

    const position = this.dataset.sizePosition;
    const sizeSelect = this.querySelector('[data-size-select]');
    const size = position ? variant['option' + position] : null;

    if (sizeSelect && size) {
      sizeSelect.value = size;
    }

    this.renderAlternatives(size || (sizeSelect && sizeSelect.value));
  }

  /** Every product of this product's family, loaded once per page (shared with the style picker). */
  loadFamily() {
    return window.rkLoadFamily(this.dataset.family);
  }

  /**
   * Shows family products that are in stock (max 12).
   * Size mode: only those in stock in the given size, linked with that size preselected.
   * Product mode (whole product sold out): any family product with at least one size in stock.
   */
  async renderAlternatives(size) {
    if (!this.altsBox || !this.altsList) return;

    const anySize = this.altsMode === 'product';
    const wanted = (size || '').toString().trim();
    this.renderToken = (this.renderToken || 0) + 1;
    const token = this.renderToken;

    if (!anySize && !wanted) {
      this.altsBox.hidden = true;
      return;
    }

    const products = await this.loadFamily();
    if (token !== this.renderToken) return; // a newer size was picked meanwhile

    const currentHandle = this.dataset.productHandle;
    const isKids = this.dataset.kids === 'true';

    const matches = products
      .filter((p) => p.handle !== currentHandle)
      .filter((p) => Boolean(p.kids) === isKids)
      .filter((p) => p.sizes && (anySize ? Object.keys(p.sizes).length > 0 : Object.prototype.hasOwnProperty.call(p.sizes, wanted)))
      .slice(0, 12);

    this.altsList.replaceChildren();

    if (matches.length === 0) {
      this.altsBox.hidden = true;
      return;
    }

    matches.forEach((p) => {
      const link = document.createElement('a');
      const separator = p.url.indexOf('?') === -1 ? '?' : '&';
      link.href = anySize ? p.url : p.url + separator + 'variant=' + p.sizes[wanted];
      link.className = 'bis-alt';
      link.title = p.title + ' - ' + p.price;
      link.setAttribute('aria-label', p.title + (anySize ? '' : ', size ' + wanted) + ', ' + p.price);

      if (p.image) {
        const img = document.createElement('img');
        img.src = p.image;
        img.alt = p.title;
        img.loading = 'lazy';
        img.width = 76;
        img.height = 76;
        link.appendChild(img);
      }

      this.altsList.appendChild(link);
    });

    this.setAltsTitle(anySize ? null : wanted);
    this.altsBox.hidden = false;
  }

  /** "Similar styles in stock" or "Similar styles in stock in size 37" (in stock in green). */
  setAltsTitle(size) {
    if (!this.altsTitle) return;

    const stock = document.createElement('span');
    stock.className = 'bis-alts__stock';
    stock.textContent = 'in stock';

    const parts = [document.createTextNode('Similar styles '), stock];

    if (size) {
      const strong = document.createElement('strong');
      strong.textContent = size;
      parts.push(document.createTextNode(' available in size '), strong);
    }

    this.altsTitle.replaceChildren(...parts);
  }

  /** Shows the selected country's flag + dial code in the compact box. */
  updateCountryDisplay() {
    const option = this.countrySelect.options[this.countrySelect.selectedIndex];
    if (!option || !option.value) return;

    if (this.countryFlag) this.countryFlag.textContent = option.dataset.flag || '';
    if (this.countryDial) this.countryDial.textContent = '+' + option.value;
  }

  /**
   * Builds the full international number, e.g. "+961 81459023".
   * Removes spaces/dashes, a leading 0 (local format, e.g. "03 123 456"),
   * and the country code if the customer typed it again.
   * Returns null if the number is too short to be real.
   */
  buildPhone(dialCode, rawLocal) {
    let local = (rawLocal || '').replace(/\D/g, '').replace(/^0+/, '');

    if (local.indexOf(dialCode) === 0 && local.length > dialCode.length + 6) {
      local = local.substring(dialCode.length);
    }

    if (local.length < 6) return null;

    return '+' + dialCode + ' ' + local;
  }

  async onSubmit(event) {
    event.preventDefault();

    this.clearBanner();

    const endpoint = this.dataset.endpoint;

    if (!endpoint) {
      this.showBanner(
        "This form isn't fully set up yet. Please contact us directly and we'll notify you by hand.",
        'error'
      );
      return;
    }

    if (!this.form.reportValidity()) {
      return;
    }

    const formData = new FormData(this.form);
    const dialCode = (formData.get('country_code') || '961').toString();
    const phone = this.buildPhone(dialCode, (formData.get('phone_local') || '').toString());

    if (!phone) {
      this.showBanner('Please enter a valid phone number.', 'error');
      return;
    }

    const payload = {
      phone: phone,
      size: (formData.get('size') || '').toString().trim(),
      productTitle: this.dataset.productTitle || '',
      productVendor: this.dataset.productVendor || '',
      productId: this.dataset.productId || '',
      productUrl: this.dataset.productUrl || '',
      pageUrl: window.location.href,
      submittedAt: new Date().toISOString()
    };

    // Show success instantly. Google Apps Script takes several seconds to
    // answer, and with mode "no-cors" its reply can't be read anyway, so
    // waiting for it only makes the customer wait.
    this.form.hidden = true;
    this.showBanner(
      "Thanks! We've saved your number and will reach out as soon as this size is back in stock.",
      'success'
    );

    // Send in the background. keepalive lets the request finish even if the
    // customer leaves the page right away.
    fetch(endpoint, {
      method: 'POST',
      mode: 'no-cors',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload)
    }).catch(() => {
      // Only a real network failure (e.g. offline) lands here: bring the
      // form back so they can try again.
      this.form.hidden = false;
      this.setLoading(false);
      this.showBanner(
        'Something went wrong sending your request. Please try again in a moment.',
        'error'
      );
    });
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;

    if (isLoading) {
      this.submitButton.dataset.originalText = this.submitButton.dataset.originalText || this.submitButton.textContent;
      this.submitButton.textContent = 'Sending...';
      this.submitButton.disabled = true;
    } else {
      this.submitButton.textContent = this.submitButton.dataset.originalText || this.submitButton.textContent;
      this.submitButton.disabled = false;
    }
  }

  showBanner(content, status) {
    if (!this.bannerRegion) return;
    this.bannerRegion.innerHTML = `<div class="banner banner--${status} text-start">${content}</div>`;
  }

  clearBanner() {
    if (this.bannerRegion) this.bannerRegion.innerHTML = '';
  }
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}